import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { EAssetType, EChannel, IAsset } from 'src/app/models/products/asset';
import { IBoundingBox } from 'src/app/models/boundingBox';
import { IComment } from 'src/app/models/comment';
import { CreateCommentResult, FilterUsersResult, ShootAssetCommentPagedResult, PostAssetCommentPagedResult } from 'src/app/models/comment-result';
import { CommentService } from 'src/app/services/comment.service';
import { AuthService } from 'src/app/services/auth.service';
import { ActivatedRoute } from '@angular/router';
import { SignalrService } from 'src/app/services/signalr.service';
import { SignalrGroup } from 'src/app/services/signalr-group';
import { ProductsService } from 'src/app/services/products.service';
import { Subject, takeUntil } from 'rxjs';
import { debounce, isEmpty } from 'lodash';
import { SignalRAssetGroups, ReviewableAssetType } from 'src/app/constants/signalr.constant';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-discussion-view',
  templateUrl: './discussion-view.component.html',
  styleUrls: ['./discussion-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class DiscussionViewComponent implements OnInit, OnChanges, OnDestroy {
  @Input() currentAsset!: IAsset;
  @Input() assetDetail!: IAsset;
  @Input() assetType = '';
  @Input() productTouchAssetId = '';
  @Input() masterSuiteTouchAssetId = '';
  @Input() organizationId = '';
  @Input() currentChannel = EChannel.EXTERNAL;
  @Input() commentBoundingBox: IBoundingBox | null = null;
  @Output() versionSelected = new EventEmitter<IComment>();
  @Output() commentHover = new EventEmitter<IComment | null>();
  @Output() clearBoundingBox = new EventEmitter<void>();

  @ViewChild('listCommentEl') private listCommentRef!: ElementRef;

  commentText = '';
  listComment: IComment[] = [];
  loading = false;
  pageNumber = 1;
  pageSize = 10;
  total = 10;
  focusId = '';
  userInfo: any = {};
  users: any = [];
  isTagging = false;
  taggedList: any = [];
  loadingAvatar: any = {};
  avatarData: any = {};
  apiBase = environment.apiBase;

  private assetId = '';
  private groupName?: string;
  private readonly destroyRef = inject(DestroyRef);
  private readonly destroy$ = new Subject();

  filterUser = debounce((value) => {
    this.commentServices
      .filterUserByKey(value, this.currentChannel === EChannel.EXTERNAL ? this.organizationId : '')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result: FilterUsersResult) => {
          this.users = result.users.slice(0, 4);
        },
        error: () => {
          this.users = [];
        },
      });
  }, 500);

  constructor(
    private signalrService: SignalrService,
    private commentServices: CommentService,
    public authService: AuthService,
    private route: ActivatedRoute,
    private productService: ProductsService,
  ) {
    this.assetId = this.route.snapshot.paramMap.get('assetId') as string;
  }

  ngOnInit(): void {
    this.initialData();
    this.commentServices.avatarData.subscribe({
      next: (data) => {
        this.avatarData = data;
      },
    });
    SignalrGroup.subscribe({
      destroyRef: this.destroyRef,
      data$: this.signalrService.connectState,
      onData: (data: boolean) => {
        if (data) {
          this.joinGroup();
          this.commentServices.setConnectionId(this.signalrService.connectionId);
        }
      },
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const ptaChange = changes['productTouchAssetId'];
    const assetChange = changes['currentAsset'];
    const channelChange = changes['currentChannel'];

    if (ptaChange && !ptaChange.firstChange && ptaChange.previousValue && ptaChange.currentValue !== ptaChange.previousValue) {
      this.signalrService.leaveGroup(this.groupName!);
      this.initialData();
      this.joinGroup();
      return;
    }

    if (assetChange && !assetChange.firstChange && assetChange.previousValue?.id !== assetChange.currentValue?.id && !this.loading) {
      this.signalrService.leaveGroup(this.groupName!);
      this.initialData();
      this.joinGroup();
      return;
    }

    if (channelChange && !channelChange.firstChange) {
      this.listComment = [];
      this.pageNumber = 1;
      this.loadComment();
      this.signalrService.leaveGroup(this.groupName!);
      this.joinGroup();
    }
  }

  ngOnDestroy(): void {
    this.signalrService.leaveGroup(this.groupName!);
    this.signalrService.clearChatBox();
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  private joinGroup(): void {
    const hubMethod = SignalRAssetGroups[this.assetType as ReviewableAssetType]?.Comment;
    if (!hubMethod) {
      return;
    }
    if (this.assetType === EAssetType.SHOOT_ASSETS) {
      this.signalrService
        .joinIdGroup(hubMethod, this.productTouchAssetId)
        ?.then((group: string) => (this.groupName = group));
    } else {
      this.signalrService
        .joinChannelGroup(hubMethod, this.productTouchAssetId, this.currentChannel)
        ?.then((group: string) => (this.groupName = group));
    }
  }

  initialData(): void {
    this.pageNumber = 1;
    this.pageSize = 10;
    this.listComment = [];
    this.isTagging = false;
    this.taggedList = [];
    this.loadComment();

    this.userInfo = this.authService.getAuthInformation();

    SignalrGroup.subscribe({
      destroyRef: this.destroyRef,
      data$: this.signalrService.chatBoxData,
      onData: (data: IComment) => {
        if (isEmpty(data)) {
          return;
        }
        const idx = this.listComment.findIndex((el) => el.id === data.id);
        if (idx > -1) {
          return;
        }
        const avatar = this.avatarData[data.authorProfileImageUrl || ''];
        if (!avatar) {
          this.getAvatar(data.authorProfileImageUrl || '');
        }
        this.listComment = [
          ...this.listComment,
          {
            ...data,
            comment: this.replaceUserNameById({
              ...data,
              userTagIds: data.tagUsers,
            }),
            version:
              this.currentAsset.id ===
              data[this.assetType === EAssetType.SHOOT_ASSETS ? 'shootAssetId' : 'postAssetId']
                ? 'Current'
                : `v${data[this.assetType === EAssetType.SHOOT_ASSETS ? 'shootAssetVersion' : 'postAssetVersion']}`,
          },
        ];
        this.scrollToBottom();
      },
    });
  }

  getAvatar(authorProfileImageUrl: string): void {
    if (!authorProfileImageUrl || this.loadingAvatar[authorProfileImageUrl]) {
      return;
    }
    this.loadingAvatar[authorProfileImageUrl] = true;
    this.commentServices.getAvatar(this.apiBase + authorProfileImageUrl).subscribe({
      next: ({ changingThisBreaksApplicationSecurity }: any) => {
        this.commentServices.setAvatar(
          authorProfileImageUrl,
          changingThisBreaksApplicationSecurity,
        );
        this.loadingAvatar[authorProfileImageUrl] = false;
      },
    });
  }

  replaceUserNameById(data: IComment): string {
    const userData = data.userTagIds || [];
    return userData.reduce((result, element) => result.replaceAll(element.id, element.name), data.comment);
  }

  loadComment(): void {
    this.loading = true;
    this.commentServices[
      this.assetType === EAssetType.SHOOT_ASSETS ? 'getShootAssetComment' : 'getPostAssetComment'
    ](
      this.assetType === EAssetType.SHOOT_ASSETS ? this.assetId : this.productTouchAssetId,
      {
        pageNumber: this.pageNumber,
        pageSize: this.pageSize,
      },
      this.currentChannel === EChannel.INTERNAL,
      this.assetType === EAssetType.POST_ASSETS ? this.masterSuiteTouchAssetId : '',
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: ShootAssetCommentPagedResult | PostAssetCommentPagedResult) => {
          const results = data.results || [];
          this.listComment = [...results.reverse(), ...this.listComment].map((el: IComment) => {
            const avatar = this.avatarData[el.authorProfileImageUrl || ''];
            if (!avatar) {
              this.getAvatar(el.authorProfileImageUrl || '');
            }
            return {
              ...el,
              comment: this.replaceUserNameById({
                ...el,
                userTagIds: el.tagUsers,
              }),
              userTagIds: el.tagUsers,
              version:
                this.currentAsset.id ===
                el[this.assetType === EAssetType.SHOOT_ASSETS ? 'shootAssetId' : 'postAssetId']
                  ? 'Current'
                  : `v${el[this.assetType === EAssetType.SHOOT_ASSETS ? 'shootAssetVersion' : 'postAssetVersion']}`,
            };
          });
          this.loading = false;
          this.pageNumber = data.pageNumber;
          this.pageSize = data.pageSize;
          this.total = data.total;
          this.scrollToBottom();
        },
        error: () => {
          this.loading = true;
        },
      });
  }

  parseCommentText(): string {
    const splitText = this.commentText.split('');
    let pair = -1;
    const processed = splitText.map((char, i) => {
      if (char === '@') {
        if (pair === -1) {
          pair = i;
          return char;
        } else {
          const result = { index: pair, clear: true };
          pair = i;
          splitText[result.index] = '';
          return char;
        }
      } else if (char === '#') {
        if (pair === -1) {
          return '';
        } else {
          pair = -1;
          return char;
        }
      }
      return char;
    });
    if (pair !== -1) {
      processed[pair] = '';
    }
    return processed.filter((el: string) => !!el).join('');
  }

  removeTag(text: string): void {
    const idx = this.commentText.indexOf(text);
    this.commentText = this.commentText.slice(0, idx - 1) + this.commentText.slice(idx);
    this.commentText =
      this.commentText.slice(0, idx - 1 + text.length) + this.commentText.slice(idx + text.length);
  }

  getUserTagged(): any[] {
    const rule = /@(.*?)#/;
    const userTagIds: any[] = [];
    const splitText = this.commentText.split(rule);
    const indices = Array.from({ length: Math.floor((splitText.length - 1) / 2) }, (_, i) => i * 2 + 1);
    indices.forEach((i) => {
      const user = this.taggedList.find((el: any) => splitText[i] === el.name);
      if (!!user) {
        userTagIds.push(user);
        this.commentText = this.commentText.replace(splitText[i], user.id);
      } else {
        this.removeTag(splitText[i]);
      }
    });
    return userTagIds;
  }

  sendComment(): void {
    if (!this.commentText) {
      return;
    }
    const idTemp = Math.floor(Math.random() * 999999);

    this.commentText = this.parseCommentText();
    const tagUsers = this.getUserTagged();
    const userTagIds = tagUsers.map((el: any) => el.id);
    this.commentText = this.commentText.replaceAll('@', '<a>');
    this.commentText = this.commentText.replaceAll('#', '</a>');

    const now = new Date();
    this.listComment = [
      ...this.listComment,
      {
        id: idTemp.toString(),
        createdAt: now.toString(),
        version: `v${this.assetDetail.versionNumber}`,
        authorUserId: this.userInfo.id,
        authorUsername: this.userInfo.name,
        authorProfileImageUrl: this.userInfo.profileImageUrl,
        comment: this.replaceUserNameById({
          comment: this.commentText.trim().replaceAll('\n', '<br/>'),
          userTagIds: tagUsers,
        } as IComment),
        organizationId: this.organizationId,
        productTouchAssetId:
          this.assetType === EAssetType.SHOOT_ASSETS ? this.assetId : this.productTouchAssetId,
        shootAssetId: this.assetDetail.id,
        shootAssetVersion: this.assetDetail.versionNumber,
        shootAssetVersionDate: new Date().getTime().toString(),
        postAssetId: this.assetDetail.id,
        postAssetVersion: this.assetDetail.versionNumber,
        postAssetVersionDate: new Date().getTime().toString(),
        updatedAt: now.toString(),
        userTagIds,
        imageReference: this.commentBoundingBox != null,
        boundingBoxReference: this.commentBoundingBox!,
      },
    ];

    const payload = {
      comment: this.commentText.trim().replaceAll('\n', '<br/>'),
      versionNumber: this.assetDetail.versionNumber,
      shootAssetId: this.assetDetail.id,
      postAssetId: this.assetDetail.id,
      userTagIds,
      imageReference: this.commentBoundingBox != null,
      boundingBoxReference: this.commentBoundingBox,
    };

    this.commentServices[
      this.assetType === EAssetType.SHOOT_ASSETS ? 'sentShootAssetComment' : 'sentPostAssetComment'
    ](
      this.assetType === EAssetType.SHOOT_ASSETS ? this.assetId : this.productTouchAssetId,
      payload,
      this.currentChannel === EChannel.INTERNAL,
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result: CreateCommentResult) => {
          const value = result.comment;
          const idx = this.listComment.findIndex((el) => el.id === idTemp.toString());
          if (idx > -1) {
            this.listComment = this.listComment.map((el, i) =>
              i === idx
                ? {
                    ...value,
                    comment: this.replaceUserNameById({
                      ...value,
                      userTagIds: value.tagUsers || tagUsers,
                    }),
                    userTagIds: value.tagUsers,
                    version:
                      this.currentAsset.id ===
                      value[this.assetType === EAssetType.SHOOT_ASSETS ? 'shootAssetId' : 'postAssetId']
                        ? 'Current'
                        : `v${value[this.assetType === EAssetType.SHOOT_ASSETS ? 'shootAssetVersion' : 'postAssetVersion']}`,
                  }
                : el,
            );
          }
        },
      });
    this.clearBoundingBox.emit();
    this.commentText = '';
    this.focusId = '';
    this.isTagging = false;
    this.taggedList = [];
    this.scrollToBottom();
  }

  triggerFunction(event: any): void {
    if (event.key === 'Enter' && !event.shiftKey && !this.isTagging) {
      event.preventDefault();
      this.sendComment();
    }
  }

  scrollToBottom(): void {
    try {
      setTimeout(() => {
        const el = document.getElementById(
          this.focusId ? this.focusId : this.listComment[this.listComment.length - 1]?.id,
        );
        if (el) {
          el.scrollIntoView();
        }
      });
    } catch (err) {}
  }

  onScroll(event: any): void {
    if (event.srcElement.scrollTop === 0) {
      if (this.pageNumber * this.pageSize < this.total) {
        this.pageNumber++;
        this.focusId = !!this.listComment[0] ? this.listComment[0].id : '';
        if (this.loading) {
          return;
        }
        this.loadComment();
      } else {
        this.focusId = '';
      }
    }
  }

  focusComment(status: boolean): void {
    this.productService.updateIsFocusComment(status);
  }

  onFilterUser(value: string): void {
    if (value.length < 1) {
      this.users = [];
    } else {
      this.filterUser(value);
    }
  }

  setOpenTagger(): void {
    this.isTagging = true;
  }

  onTagged(value: any): void {
    this.taggedList = [...this.taggedList, value];
  }

  setCloseTagger(): void {
    setTimeout(() => {
      this.isTagging = false;
      this.commentText += '#';
    });
  }
}
